import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { IndicatorAnswer } from "../help/types";
import type { QrSessionContext } from "../qr/types";
import {
  getSelectableLanguages,
  isLanguageSelectable,
  languagesConfig,
} from "../i18n/languages";
import { loadLocale, t, type LocaleTable } from "../i18n/loadLocale";

function resolveInitialLanguage(): string {
  const enabled = getSelectableLanguages();
  if (enabled.length === 0) {
    return languagesConfig.defaultLanguage;
  }
  if (enabled.length === 1) {
    return enabled[0].code;
  }
  if (enabled.some((l) => l.code === languagesConfig.defaultLanguage)) {
    return languagesConfig.defaultLanguage;
  }
  return enabled[0].code;
}

/** In-memory resume after /resources — never persisted, never in URLs. */
export type FlowBookmark = {
  view: string;
  questionIndex: number;
  history: string[];
};

type SessionContextValue = {
  languageCode: string;
  locale: LocaleTable;
  setLanguageCode: (code: string) => void;
  translate: (key: string) => string;
  helpAnswers: Record<string, IndicatorAnswer>;
  setHelpAnswer: (indicatorId: string, value: IndicatorAnswer) => void;
  clearHelpAnswers: () => void;
  concernAnswers: Record<string, IndicatorAnswer>;
  setConcernAnswer: (indicatorId: string, value: IndicatorAnswer) => void;
  clearConcernAnswers: () => void;
  hospitalityAnswers: Record<string, IndicatorAnswer>;
  setHospitalityAnswer: (indicatorId: string, value: IndicatorAnswer) => void;
  clearHospitalityAnswers: () => void;
  resourceCategories: string[] | null;
  setResourceCategories: (categories: string[] | null) => void;
  resourceReturnPath: string;
  setResourceReturnPath: (path: string) => void;
  helpBookmark: FlowBookmark | null;
  setHelpBookmark: (bookmark: FlowBookmark | null) => void;
  concernBookmark: FlowBookmark | null;
  setConcernBookmark: (bookmark: FlowBookmark | null) => void;
  hospitalityBookmark: FlowBookmark | null;
  setHospitalityBookmark: (bookmark: FlowBookmark | null) => void;
  /** Minimal QR routing context — no code, partner, or placement. Memory only. */
  qrContext: QrSessionContext | null;
  setQrContext: (context: QrSessionContext | null) => void;
  clearQrContext: () => void;
  clearSession: () => void;
};

const SessionContext = createContext<SessionContextValue | null>(null);

/**
 * In-memory session only. No localStorage / cookies / IndexedDB / sessionStorage.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [languageCode, setLanguageCodeState] = useState(resolveInitialLanguage);
  const [generation, setGeneration] = useState(0);
  const [helpAnswers, setHelpAnswers] = useState<
    Record<string, IndicatorAnswer>
  >({});
  const [concernAnswers, setConcernAnswers] = useState<
    Record<string, IndicatorAnswer>
  >({});
  const [hospitalityAnswers, setHospitalityAnswers] = useState<
    Record<string, IndicatorAnswer>
  >({});
  const [resourceCategories, setResourceCategoriesState] = useState<
    string[] | null
  >(null);
  const [resourceReturnPath, setResourceReturnPathState] =
    useState<string>("/");
  const [helpBookmark, setHelpBookmarkState] = useState<FlowBookmark | null>(
    null,
  );
  const [concernBookmark, setConcernBookmarkState] =
    useState<FlowBookmark | null>(null);
  const [hospitalityBookmark, setHospitalityBookmarkState] =
    useState<FlowBookmark | null>(null);
  const [qrContext, setQrContextState] = useState<QrSessionContext | null>(
    null,
  );

  const setLanguageCode = useCallback((code: string) => {
    if (!isLanguageSelectable(code)) {
      return;
    }
    setLanguageCodeState(code);
  }, []);

  const setHelpAnswer = useCallback(
    (indicatorId: string, value: IndicatorAnswer) => {
      setHelpAnswers((prev) => ({ ...prev, [indicatorId]: value }));
    },
    [],
  );

  const clearHelpAnswers = useCallback(() => {
    setHelpAnswers({});
  }, []);

  const setConcernAnswer = useCallback(
    (indicatorId: string, value: IndicatorAnswer) => {
      setConcernAnswers((prev) => ({ ...prev, [indicatorId]: value }));
    },
    [],
  );

  const clearConcernAnswers = useCallback(() => {
    setConcernAnswers({});
  }, []);

  const setHospitalityAnswer = useCallback(
    (indicatorId: string, value: IndicatorAnswer) => {
      setHospitalityAnswers((prev) => ({ ...prev, [indicatorId]: value }));
    },
    [],
  );

  const clearHospitalityAnswers = useCallback(() => {
    setHospitalityAnswers({});
  }, []);

  const setResourceCategories = useCallback((categories: string[] | null) => {
    setResourceCategoriesState(categories);
  }, []);

  const setResourceReturnPath = useCallback((path: string) => {
    setResourceReturnPathState(path);
  }, []);

  const setHelpBookmark = useCallback((bookmark: FlowBookmark | null) => {
    setHelpBookmarkState(bookmark);
  }, []);

  const setConcernBookmark = useCallback((bookmark: FlowBookmark | null) => {
    setConcernBookmarkState(bookmark);
  }, []);

  const setHospitalityBookmark = useCallback(
    (bookmark: FlowBookmark | null) => {
      setHospitalityBookmarkState(bookmark);
    },
    [],
  );

  const setQrContext = useCallback((context: QrSessionContext | null) => {
    setQrContextState(context);
  }, []);

  const clearQrContext = useCallback(() => {
    setQrContextState(null);
  }, []);

  const clearSession = useCallback(() => {
    setLanguageCodeState(resolveInitialLanguage());
    setHelpAnswers({});
    setConcernAnswers({});
    setHospitalityAnswers({});
    setResourceCategoriesState(null);
    setResourceReturnPathState("/");
    setHelpBookmarkState(null);
    setConcernBookmarkState(null);
    setHospitalityBookmarkState(null);
    setQrContextState(null);
    setGeneration((g) => g + 1);
  }, []);

  const locale = useMemo(() => loadLocale(languageCode), [languageCode]);

  const value = useMemo<SessionContextValue>(
    () => ({
      languageCode,
      locale,
      setLanguageCode,
      translate: (key: string) => t(locale, key),
      helpAnswers,
      setHelpAnswer,
      clearHelpAnswers,
      concernAnswers,
      setConcernAnswer,
      clearConcernAnswers,
      hospitalityAnswers,
      setHospitalityAnswer,
      clearHospitalityAnswers,
      resourceCategories,
      setResourceCategories,
      resourceReturnPath,
      setResourceReturnPath,
      helpBookmark,
      setHelpBookmark,
      concernBookmark,
      setConcernBookmark,
      hospitalityBookmark,
      setHospitalityBookmark,
      qrContext,
      setQrContext,
      clearQrContext,
      clearSession,
    }),
    [
      languageCode,
      locale,
      setLanguageCode,
      helpAnswers,
      setHelpAnswer,
      clearHelpAnswers,
      concernAnswers,
      setConcernAnswer,
      clearConcernAnswers,
      hospitalityAnswers,
      setHospitalityAnswer,
      clearHospitalityAnswers,
      resourceCategories,
      setResourceCategories,
      resourceReturnPath,
      setResourceReturnPath,
      helpBookmark,
      setHelpBookmark,
      concernBookmark,
      setConcernBookmark,
      hospitalityBookmark,
      setHospitalityBookmark,
      qrContext,
      setQrContext,
      clearQrContext,
      clearSession,
    ],
  );

  return (
    <SessionContext.Provider value={value}>
      <div key={generation} className="session-root">
        {children}
      </div>
    </SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error("useSession must be used within SessionProvider");
  }
  return ctx;
}
